import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./LocketPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Locket" (/locket) — a FEATURE-SPECIFIC paywall, built to the standing
// #paywalls directive: SECURITY & PRIVACY.
//
// The hero is an object no other paywall here uses: a fine gold HEIRLOOM LOCKET
// resting on a jeweler's cushion. Closed, it is a mirror-polished, engraved case
// anyone may hold but no one else can open. Tap the catch and it springs on its
// hinge into a two-panel spread — the right leaf holds your real memories in warm
// colour, the left leaf is an engraved plate whose four HALLMARKS (the real
// Premium protections) light one after another in brand-blue enamel: Only you ·
// Encrypted · Private · In sync.
//
// The one mechanic no other paywall here uses — a SPRING-HINGE locket that opens
// and closes on a tap (not a lock, seal, zip, dial, deadbolt, slider, scrub,
// hold-to-reveal, key-turn, page-turn, buckle, stitch or riffle). The whole
// pitch is CONTRAST + SOLE CUSTODY: closed-to-everyone vs. open-only-for-you.
// Register: treasured / jewel-grade intimate — a keepsake, not a fortress; a
// deliberate turn away from the cold-security lane.
//
// Honest & App-Store compliant: the featured, most-prominent price is the YEARLY
// plan (7-day free trial, per-month broken out, honest Save %); monthly is a
// small secondary line. No scarcity/countdown. No data-loss claim — you hold the
// only key, nothing is ever locked away from you or deleted; Premium only ADDS
// protection. Reuses only the house font, colour tokens and the bundled byheart
// memory photos. Full light + dark, reduced-motion aware. Renders standalone on
// debug data; no native injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The memories that live inside the locket (the right leaf). Reused bundled
// byheart photos — the only assets this paywall pulls in.
const MEMORIES = ["m09", "m03", "m14", "m07"].map(photo)

// The four REAL Premium protections, engraved as hallmarks on the inside of the
// lid. Each lights (blue enamel) in turn as the locket opens.
type Hallmark = { id: string; label: string; sub: string; icon: JSX.Element }
const HALLMARKS: Hallmark[] = [
  {
    id: "only-you",
    label: "Only you",
    sub: "Face ID / PIN",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M7.6 10V7.7a4.4 4.4 0 0 1 8.8 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="5.2"
          y="10"
          width="13.6"
          height="9.4"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.3" r="1.35" fill="var(--lk-seal-hole)" />
      </svg>
    ),
  },
  {
    id: "encrypted",
    label: "Encrypted",
    sub: "End-to-end",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M12 3.3l6.8 2.9v4.9c0 4.5-2.9 7.4-6.8 8.8-3.9-1.4-6.8-4.3-6.8-8.8V6.2L12 3.3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.8 12.1l2.1 2.1 4.3-4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "private",
    label: "Private",
    sub: "Off Camera Roll",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M2.6 12S6 6 12 6s9.4 6 9.4 6-3.4 6-9.4 6-9.4-6-9.4-6z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="2.5" fill="currentColor" />
        <path
          d="M4 4l16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "in-sync",
    label: "In sync",
    sub: "Your devices",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M5 12a7 7 0 0 1 11.9-5M19 12A7 7 0 0 1 7.1 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M16.4 3.6V7h-3.4M7.6 20.4V17h3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
]

function LocketPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [open, setOpen] = useState(false)

  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Header inset blends with the top of the page gradient; footer inset matches
    // the pricing panel so the safe-area bar is invisible against it.
    actions.setHeaderInsetColor(darkMode ? "#181923" : "#fbf7ee")
    actions.setFooterInsetColor(darkMode ? "#171922" : "#fffdf9")
  }, [actions, darkMode])

  // Auto-demo: the locket springs open a beat after load so the payoff reads in
  // ~3s, then the user can tap it closed/open. Reduced motion starts open.
  useEffect(() => {
    if (reduce) {
      setOpen(true)
      return
    }
    const t = setTimeout(() => setOpen(true), 850)
    return () => clearTimeout(t)
  }, [reduce])

  // A gentle jewel sheen that follows the pointer across the cushion (skipped for
  // reduced motion). Written straight to a CSS var so it never re-renders React.
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (reduce) return
      const root = rootRef.current
      if (!root) return
      const r = root.getBoundingClientRect()
      const x = ((e.clientX - r.left) / r.width - 0.5) * 2
      const y = ((e.clientY - r.top) / r.height - 0.5) * 2
      root.style.setProperty("--tilt-x", `${(-y * 4).toFixed(2)}deg`)
      root.style.setProperty("--tilt-y", `${(x * 5).toFixed(2)}deg`)
      root.style.setProperty("--sheen", `${(50 + x * 40).toFixed(1)}%`)
    },
    [reduce],
  )

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_locket_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Protect my memories"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""} ${
        open ? styles.open : ""
      }`}
      ref={rootRef}
      onPointerMove={onPointerMove}
    >
      <div className={styles.grain} aria-hidden />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M8 10V7.7a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--lk-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker}>Your private locket</span>
          <h1 className={styles.title}>
            Kept where only
            <br />
            you can open it.
          </h1>
          <p className={styles.sub}>
            Your private photos live in a locket only your Face&nbsp;ID or PIN
            opens — a sealed case to anyone else.
          </p>
        </header>
      </div>

      {/* ── The locket on its cushion ───────────────────────────────────── */}
      <div className={styles.stage}>
        <div className={styles.pedestal} aria-hidden />

        <button
          className={styles.locket}
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close the locket" : "Open the locket"}
          aria-pressed={open}
        >
          {/* Chain rising from the bail, off the top */}
          <svg className={styles.chain} viewBox="0 0 110 64" aria-hidden>
            <path
              d="M20 4 C 42 30, 50 54, 55 58 C 60 54, 68 30, 90 4"
              fill="none"
              stroke="url(#lk-chain)"
              strokeWidth="3.4"
              strokeLinecap="round"
              strokeDasharray="1.5 6.5"
            />
            <defs>
              <linearGradient id="lk-chain" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="var(--lk-gold-2)" />
                <stop offset="0.5" stopColor="var(--lk-gold-hi)" />
                <stop offset="1" stopColor="var(--lk-gold-2)" />
              </linearGradient>
            </defs>
          </svg>
          <span className={styles.bail} aria-hidden />

          {/* Base leaf: the memories (revealed when the lid swings open) */}
          <span className={styles.interior} aria-hidden>
            <span className={styles.interiorRim}>
              <span className={styles.memGrid}>
                {MEMORIES.map((src) => (
                  <span
                    key={src}
                    className={styles.mem}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                ))}
              </span>
              <span className={styles.onlyYou}>
                <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden>
                  <path
                    d="M8 10V7.7a4 4 0 0 1 8 0V10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="5.6"
                    y="10"
                    width="12.8"
                    height="8.8"
                    rx="2.4"
                    fill="currentColor"
                  />
                </svg>
                Only you
              </span>
            </span>
          </span>

          {/* The lid, hinged on its left edge */}
          <span className={styles.hinge}>
            {/* Outside of the lid — engraved, mirror-polished, closed to all */}
            <span className={styles.coverFront}>
              <span className={styles.guilloche} aria-hidden />
              <span className={styles.sheen} aria-hidden />
              <span className={styles.crest} aria-hidden>
                <svg viewBox="0 0 40 40" width="42" height="42" aria-hidden>
                  <path
                    d="M14 18v-3.2a6 6 0 0 1 12 0V18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="10.5"
                    y="18"
                    width="19"
                    height="13.5"
                    rx="3.4"
                    fill="currentColor"
                  />
                  <circle
                    cx="20"
                    cy="24.3"
                    r="1.9"
                    fill="var(--lk-crest-hole)"
                  />
                </svg>
              </span>
              <span className={styles.engrave}>
                PRIVATE&nbsp;·&nbsp;ONLY YOU
              </span>
            </span>

            {/* Inside of the lid — the engraved hallmark plate */}
            <span className={styles.coverBack}>
              <span className={styles.plateHead}>Kept for only you</span>
              <span className={styles.seals}>
                {HALLMARKS.map((h) => (
                  <span key={h.id} className={styles.seal}>
                    <span className={styles.sealIcon}>{h.icon}</span>
                    <span className={styles.sealText}>
                      <b>{h.label}</b>
                      <i>{h.sub}</i>
                    </span>
                    <span className={styles.sealTick} aria-hidden>
                      <svg viewBox="0 0 24 24" width="10" height="10">
                        <path
                          d="M5 12.5l4.2 4.2L19 7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </span>
                ))}
              </span>
            </span>
          </span>
        </button>

        {/* State caption + tap affordance */}
        <div className={styles.caption} aria-live="polite">
          {open ? (
            <span>
              <i className={styles.capDot} data-on /> Open — your memories, kept
              for only you
            </span>
          ) : (
            <span>
              <i className={styles.capDot} /> Closed — a sealed case no one else
              can open
            </span>
          )}
        </div>
      </div>

      {/* ── Pricing + CTA (yearly featured) ─────────────────────────────── */}
      <div className={styles.panel} style={{ paddingBottom: bottomInset + 22 }}>
        <div className={styles.legend}>
          {HALLMARKS.map((h) => (
            <span key={h.id} className={styles.chip}>
              <span className={styles.chipIcon}>{h.icon}</span>
              {h.label}
            </span>
          ))}
        </div>

        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>
        <p className={styles.reassure}>
          Your memories are always yours — nothing is ever deleted or locked
          away. You hold the only key; Premium only adds protection.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default LocketPaywall
